import type { TRPCOptionsProxy } from "@trpc/tanstack-react-query";

import {
  queryOptions,
  type QueryClient,
} from "@tanstack/react-query";
import {
  Outlet,
  HeadContent,
  Scripts,
  createRootRouteWithContext,
} from "@tanstack/react-router";
import {
  createServerFn,
  createMiddleware,
} from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
// src/routes/__root.tsx
import { useEffect, type ReactNode } from "react";

import type { AppRouter } from "@/infrastructure/trpc/router";

import { auth } from "@/auth/auth.server";
import { env } from "@/env/public-env";
import { useHydrated } from "@/hooks/use-hydrated";
import { useSetSentryContext } from "@/hooks/use-set-sentry-context";
import { NotFoundError } from "@/infrastructure/components/errors/not-found-error";
import { getThemeServerFn } from "@/infrastructure/theme";
import { ThemeProvider } from "@/shared/layout/mode/theme-provider";
import { Toaster } from "@/shared/ui/sonner";
// @ts-ignore https://github.com/TanStack/router/issues/3306
import appCss from "@/styles/app.css?url";
import {
  TanStackRouterDevtools,
  ReactQueryDevtools,
} from "@/utils/dev-tools";
import { seo, getCanonicalLinks } from "@/utils/seo";

type RootContext = {
  queryClient: QueryClient;
  trpc: TRPCOptionsProxy<AppRouter>;
};

// SSR timing middleware
const ssrTimingMiddleware = createMiddleware().server(
  async ({ request, next }) => {
    const startTime = Date.now();
    const url = new URL(request.url);
    const routePath = url.pathname;

    try {
      const result = await next();
      // const duration = Date.now() - startTime;

      // console.log(
      //   `[SSR] ${request.method} ${routePath} completed in ${duration}ms`
      // );

      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      console.error(
        `[SSR] ${request.method} ${routePath} failed after ${duration}ms:`,
        error
      );
      throw error;
    }
  }
);

const getServerSession = createServerFn({
  method: "GET",
}).handler(async () => {
  // During prerendering, skip session check (no DB access available)
  if (process.env.VITE_PRERENDER === "true") {
    return null;
  }

  const { headers } = getRequest()!;
  const session = await auth.api.getSession({ headers });

  // Extract only serializable parts to avoid "Function is not serializable" error
  if (!session) {
    return null;
  }

  return {
    session: session.session,
    user: session.user,
  };
});

const sessionQueryOptions = queryOptions({
  queryKey: ["auth", "session"],
  queryFn: () => getServerSession(),
  staleTime: 5 * 60 * 1000,
  gcTime: 30 * 60 * 1000,
});

const themeQueryOptions = queryOptions({
  queryKey: ["theme"],
  queryFn: () => getThemeServerFn(),
  staleTime: Infinity,
});

export const Route =
  createRootRouteWithContext<RootContext>()({
    beforeLoad: async ({ context }) => {
      const session = await context.queryClient.query({
        ...sessionQueryOptions,
        staleTime: "static",
      });

      return { session };
    },
    server: {
      middleware: [ssrTimingMiddleware],
    },
    head: () => ({
      meta: [
        { charSet: "utf-8" },
        {
          name: "viewport",
          content: "width=device-width, initial-scale=1",
        },
        ...seo({
          title: "vivus",
          description: "vivus",
          keywords: "vivus",
          image: `${env.VITE_BASE_URL}/api/og/page/homepage.png`,
          logo: `${env.VITE_BASE_URL}/logo.png`,
        }),
      ],
      links: [
        {
          rel: "stylesheet",
          href: appCss,
        },
        {
          rel: "manifest",
          href: "/manifest.json",
        },
        {
          rel: "icon",
          type: "image/svg+xml",
          href: "/favicon.svg",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "96x96",
          href: "/favicon-96x96.png",
        },
        {
          rel: "apple-touch-icon",
          sizes: "180x180",
          href: "/apple-icon.png",
        },
        {
          rel: "icon",
          href: "/favicon.ico",
        },
        ...getCanonicalLinks(env.VITE_BASE_URL),
      ],
    }),
    component: RootComponent,
    notFoundComponent: () => <NotFoundError />,
    loader: async ({ context }) => {
      const theme = await context.queryClient.query({
        ...themeQueryOptions,
        staleTime: "static",
      });
      return { theme };
    },
  });

function RootComponent() {
  const { theme } = Route.useLoaderData();
  return (
    <ThemeProvider theme={theme.preference}>
      <RootDocument>
        <Outlet />
      </RootDocument>
    </ThemeProvider>
  );
}

function RootDocument({
  children,
}: Readonly<{ children: ReactNode }>) {
  const { theme } = Route.useLoaderData();
  const { session } = Route.useRouteContext();
  useSetSentryContext(session?.user);

  const hydrated = useHydrated();

  useEffect(() => {
    if (import.meta.env.DEV) {
      void import("react-grab");
    }
  }, []);

  return (
    <html
      lang="en"
      className={theme.resolved}
      data-hydrated={hydrated ? "true" : "false"}
    >
      <head>
        <HeadContent />
      </head>
      <body className="antialiased">
        <div className="root">
          {children}
          <Toaster />
          <TanStackRouterDevtools position="bottom-left" />
          <ReactQueryDevtools buttonPosition="bottom-left" />
        </div>
        <Scripts />
      </body>
    </html>
  );
}
